import React from 'react';

interface EmailCnpjInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

const formatCnpj = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
  if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
  if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
  return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
};

const isEmail = (value: string): boolean => {
  return value.includes('@');
};

export const EmailCnpjInput: React.FC<EmailCnpjInputProps> = ({
  value,
  onChange,
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Se contém @, é email - não formatar
    if (isEmail(inputValue)) {
      onChange(inputValue);
      return;
    }
    
    // Se começa com letra ou contém caracteres não numéricos além de formatação, pode ser email
    // Mas se já tem números suficientes, tratar como CNPJ
    const numbers = inputValue.replace(/\D/g, '');
    
    // Se tem menos de 3 dígitos e contém letras, pode ser início de email
    if (numbers.length < 3 && /[a-zA-Z]/.test(inputValue)) {
      onChange(inputValue);
      return;
    }
    
    // Caso contrário, tratar como CNPJ e formatar
    const limitedRaw = numbers.slice(0, 14); // Limitar a 14 dígitos (CNPJ)
    const formatted = formatCnpj(limitedRaw);
    onChange(formatted);
  };

  const getPlaceholder = () => {
    if (props.placeholder !== undefined) {
      return props.placeholder;
    }
    return '';
  };

  const getMaxLength = () => {
    if (isEmail(value)) {
      return 255; // Email pode ser longo
    }
    return 18; // CNPJ formatado: 00.000.000/0000-00 (máximo)
  };

  const getType = () => {
    if (isEmail(value)) {
      return 'email';
    }
    return 'text';
  };

  return (
    <input
      {...props}
      type={getType()}
      value={value}
      onChange={handleChange}
      placeholder={getPlaceholder()}
      maxLength={getMaxLength()}
    />
  );
};
